import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Tv, Loader2, Wifi, Play, ArrowLeft, RefreshCw } from "lucide-react";
import { postCastPair, fetchCastStatus, postCastAck } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

/**
 * TVCastReceiver (/tv/cast)
 * ─────────────────────────
 * Full-screen "receiver" mode designed for the smart TV browser.
 *
 *   1. On mount, requests a fresh 4-digit code from the backend.
 *   2. Displays it hugely, waits for a phone to hit /cast/link with the
 *      same code.
 *   3. Polls /cast/status every 2s. When a Watch command arrives, the TV
 *      auto-navigates to `/watch/{snapshot_id}` (or the requested mode's
 *      route), then acks so the same command doesn't loop.
 *   4. Never asks for user input on the TV — the couch experience.
 */
const POLL_MS = 2000;

export default function TVCastReceiver() {
  const [code, setCode] = useState(null);
  const [linked, setLinked] = useState(false);
  const [error, setError] = useState(null);
  const [refreshingCode, setRefreshingCode] = useState(false);
  const navigate = useNavigate();
  const pollRef = useRef(null);
  const lastCommandRef = useRef(null);

  const requestCode = async () => {
    setRefreshingCode(true);
    setError(null);
    try {
      const r = await postCastPair();
      setCode(r.code);
      setLinked(false);
      lastCommandRef.current = null;
      trackEvent("tv_cast_pair", { code: r.code });
    } catch (e) {
      setError("Couldn't reach TimeCapsule — check your TV's WiFi and reload.");
    } finally {
      setRefreshingCode(false);
    }
  };

  useEffect(() => {
    requestCode();
  }, []);

  useEffect(() => {
    if (!code) return;
    let consecutiveErrors = 0;
    // Poll for status + commands
    const tick = async () => {
      try {
        const r = await fetchCastStatus(code);
        consecutiveErrors = 0;
        if (r.linked && !linked) setLinked(true);
        // Command may be null if TV already acked, or a fresh dict
        const cmd = r.command;
        if (cmd && cmd.snapshot_id && cmd.issued_at !== lastCommandRef.current) {
          lastCommandRef.current = cmd.issued_at;
          trackEvent("tv_cast_command_received", { snapshot_id: cmd.snapshot_id, mode: cmd.mode });
          // ACK first so a slow render doesn't cause a re-fire
          postCastAck(code).catch(() => {});
          const mode = cmd.mode || "cinema";
          if (mode === "cinema") navigate(`/watch/${cmd.snapshot_id}`);
          else if (mode === "newspaper") navigate(`/snapshot/${cmd.snapshot_id}`);
          else if (mode === "ambient") navigate(`/tv/ambient`);
        }
      } catch (err) {
        // Only give up + request a new code on a 404 (session actually gone).
        // Transient blips (5xx / offline) — keep polling with the same code.
        const status = err?.response?.status;
        if (status === 404) {
          setLinked(false);
          setCode(null);
          requestCode();
        } else {
          consecutiveErrors += 1;
          // Give up after ~30s of solid failures
          if (consecutiveErrors >= 15) {
            setError("Lost connection to TimeCapsule — checking network…");
          }
        }
      }
    };
    tick();
    pollRef.current = setInterval(tick, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [code, linked, navigate]);

  return (
    <div
      className="fixed inset-0 bg-ink text-paper overflow-hidden flex flex-col"
      data-testid="tv-cast-receiver"
    >
      {/* Ambient background — subtle radial + film grain */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(186,63,29,0.28), transparent 60%), radial-gradient(circle at 80% 80%, rgba(58,74,102,0.35), transparent 65%)",
        }}
      />
      <div className="absolute inset-0 opacity-[0.07] pointer-events-none paper-grain" />

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-6 sm:px-10 py-4 sm:py-6">
        <Link
          to="/tv"
          className="inline-flex items-center gap-1.5 border-2 border-paper/40 px-3 py-2 font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-paper hover:text-ink"
          data-testid="tv-cast-receiver-exit"
        >
          <ArrowLeft size={12} /> Exit
        </Link>
        <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-paper/60 flex items-center gap-2">
          <Tv size={14} /> TimeCapsule 24 · Receiver
        </div>
        <button
          onClick={requestCode}
          disabled={refreshingCode}
          className="inline-flex items-center gap-1.5 border-2 border-paper/40 px-3 py-2 font-mono uppercase tracking-widest text-[10px] sm:text-xs hover:bg-paper hover:text-ink disabled:opacity-40"
          data-testid="tv-cast-refresh-btn"
          title="Get a new pairing code"
        >
          <RefreshCw size={12} className={refreshingCode ? "animate-spin" : ""} />
          <span className="hidden sm:inline">New code</span>
        </button>
      </div>

      {/* Main stage */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 text-center">
        {error ? (
          <div className="text-press-red font-mono uppercase tracking-widest text-sm" data-testid="tv-cast-error">
            {error}
          </div>
        ) : !code ? (
          <div className="flex items-center gap-2 font-mono uppercase tracking-widest text-[11px] text-paper/60">
            <Loader2 size={14} className="animate-spin" /> Getting a code…
          </div>
        ) : (
          <>
            {linked ? (
              <>
                <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-paper/60 mb-3 flex items-center gap-2">
                  <Wifi size={14} className="text-emerald-400" /> Paired · Waiting for your pick
                </div>
                <div className="font-headline uppercase font-black leading-none tracking-tighter text-5xl sm:text-7xl md:text-8xl mb-6">
                  Ready.
                </div>
                <div className="max-w-xl font-body italic text-base sm:text-xl text-paper/70">
                  Pick any date on your phone — it plays right here on the big screen.
                </div>
                <div className="mt-8 flex items-center gap-2 font-mono uppercase tracking-widest text-[10px] text-paper/40">
                  <Loader2 size={12} className="animate-spin" /> Listening…
                </div>
              </>
            ) : (
              <>
                <div className="font-mono uppercase tracking-[0.35em] text-[10px] sm:text-xs text-paper/60 mb-3">
                  On your phone, tap &ldquo;Watch on TV&rdquo; → enter this code
                </div>
                <div
                  className="font-headline font-black tracking-[0.15em] leading-none text-8xl sm:text-[10rem] md:text-[14rem] text-paper mb-4 select-none"
                  style={{ textShadow: "0 4px 40px rgba(186,63,29,0.35)" }}
                  data-testid="tv-cast-code"
                >
                  {code}
                </div>
                <div className="font-mono uppercase tracking-[0.3em] text-[10px] sm:text-xs text-paper/50">
                  Any TimeCapsule tab on the same account · works over any WiFi
                </div>
                <div className="mt-8 flex items-center gap-2 text-paper/50 font-mono uppercase tracking-widest text-[10px]">
                  <Loader2 size={12} className="animate-spin" /> Waiting for pairing…
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Bottom hint */}
      <div className="relative z-10 px-6 sm:px-10 py-4 border-t border-paper/10 flex flex-col sm:flex-row items-center justify-between gap-2 text-paper/40 font-mono uppercase tracking-widest text-[10px]">
        <div>
          Setup guide: <span className="text-paper/70">timecapsule24.tv/tv</span>
        </div>
        <div className="flex items-center gap-1.5">
          <Play size={10} /> Phone → TV in one tap
        </div>
      </div>
    </div>
  );
}
